import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Checkbox
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PathList } from "../../Common/Routes/Path";
import AxiosInstance from "../../Common/Api/ApiHelper";
import {apiList} from "../../Common/Api/ApiList";
import {toast} from "react-toastify";
import ErrorToaster from "../../Component/ErrorToaster";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";

const AddBadgeScreen = () => {
    const navigate = useNavigate();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [criteriaOptions, setCriteriaOptions] = useState([]);

    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const [badgeFile, setBadgeFile] = useState(null);

    const initial = {
        badge_name: "",
        description: "",
        criteria_type: "",
        criteria_value: "",
        xp_points: "",
        user_type: "",
        is_active: "",
        category_id: "",
        sub_category_id: "",
        rating_value: ""
    };

    const [badgeData, setBadgeData] = useState(initial);

    const [errors, setErrors] = useState({
        badge_name: "",
        description: "",
        criteria_type: "",
        xp_points: "",
        is_active: "",
        user_type: "",
        category_id: "",
        sub_category_id: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBadgeData({ ...badgeData, [name]: value });
    };

    const handleFileChange = (e) => {
        setBadgeFile(e.target.files[0]);
    };

    useEffect(() => {
        const fetchCriteriaTypes = async () => {
            try {
                const response = await AxiosInstance.get(apiList?.CriteriaTypes);
                const criteriaArray = Array.isArray(response.data?.r?.data)
                    ? response.data.r.data
                    : [];

                setCriteriaOptions(criteriaArray);
            } catch (error) {
                console.error("Failed to fetch criteria types", error);
                setCriteriaOptions([]);
            }
        };

        fetchCriteriaTypes();
    }, []);

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                const response = await AxiosInstance.get(apiList?.getCategory);
                const categoryArray = Array.isArray(response.data?.r)
                    ? response.data.r
                    : [];

                setCategories(categoryArray);
            } catch (error) {
                console.error("Failed to fetch CategoryOptions", error);
                setCategories([]);
            }
        };

        fetchCategory();
    }, []);


    const handleCategoryChange = (event) => {
        const selectedCategoryId = event.target.value;

        setBadgeData({
            ...badgeData,
            category_id: selectedCategoryId,
            sub_category_id: ""
        });

        const selectedCategory = categories.find(
            (cat) => cat.cat_id === selectedCategoryId
        );

        setSubCategories(selectedCategory ? selectedCategory.sub_cat_id : []);
    };

    const handleSubCategoryChange = (event) => {
        setBadgeData({
            ...badgeData,
            sub_category_id: event.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        const newErrors = {};

        if (!badgeData.badge_name) newErrors.badge_name = "Name is required";
        if (!badgeData.description) newErrors.description = "Description is required";
        if (!badgeData.criteria_type) newErrors.criteria_type = "Criteria Type is required";
        if (!badgeData.xp_points) newErrors.xp_points = "XP Points is required";
        if (!badgeData.is_active) newErrors.is_active = "Status is required";
        if (!badgeData.user_type) newErrors.user_type = "User Type is required";
        if (!badgeData.category_id) newErrors.category_id = "Category is required";
        if (!badgeData.sub_category_id) newErrors.sub_category_id = "Sub Category is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        } else {
            setErrors({});
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();

            Object.keys(badgeData).forEach((key) => {
                formData.append(key, badgeData[key]);
            });

            if (badgeFile) {
                formData.append("badge_icon", badgeFile);
            }

            const response = await AxiosInstance.post(apiList?.AddBadge, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.data.s === 1) {
                toast.success("Badge saved successfully");

                setTimeout(() => {
                    navigate(PathList.Badge);
                }, 1000);
            } else {
                toast.error(response.data.m);
            }
        } catch (error) {
            ErrorToaster(error);
        }
        finally {
            setIsSubmitting(false);
        }
        setBadgeData(initial);
    }

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5, // adjust spacing as needed
                    pb: 2,
                }}
            >
                <ArrowBackIosRoundedIcon
                    sx={{
                        cursor: "pointer",
                        "&:hover": {
                            color: "#3C75D4",
                        },
                    }}
                    onClick={() => navigate(PathList.Badge)}
                />
                <Typography variant="h6" sx={{ fontSize: "28px" }}>
                    Add Badge
                </Typography>
            </Box>

            {/* Form Card */}
            <Card sx={{ mt: 3, p: 2 }}>
                <CardContent>
                    <form style={{width: "100%"}} onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Name</Typography>
                                <TextField
                                    name="badge_name"
                                    placeholder="Enter badge name"
                                    fullWidth
                                    value={badgeData.badge_name}
                                    onChange={handleChange}
                                />
                                {errors.badge_name && (
                                    <Typography sx={{ color: "red", fontSize: "14px" }}>
                                        {errors.badge_name}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Criteria</Typography>
                                    <Select
                                        name="criteria_type"
                                        value={badgeData.criteria_type}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select Criteria
                                        </MenuItem>
                                        {criteriaOptions.map((option) => (
                                            <MenuItem key={option.id} value={option.id}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors.criteria_type && (
                                        <Typography sx={{ color: "red", fontSize: "14px" }}>
                                            {errors.criteria_type}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            {badgeData.criteria_type !== 6 && (
                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Target</Typography>
                                <TextField
                                    name="criteria_value"
                                    type="number"
                                    placeholder="Enter criteria value"
                                    fullWidth
                                    value={badgeData.criteria_value}
                                    onChange={handleChange}
                                />
                            </Grid>
                            )}

                            {badgeData.criteria_type === 4 && (
                                <Grid item xs={12} md={4}>
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Rating</Typography>
                                    <TextField
                                        name="rating_value"
                                        type="text"
                                        placeholder="Enter rating value"
                                        fullWidth
                                        value={badgeData.rating_value}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                                        Category
                                    </Typography>
                                    <Select
                                        name="category_id"
                                        value={badgeData.category_id}
                                        onChange={handleCategoryChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select Category
                                        </MenuItem>
                                        {categories.map((cat) => (
                                            <MenuItem key={cat.cat_id} value={cat.cat_id}>
                                                {cat.cat_name}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors.category_id && (
                                        <Typography sx={{ color: "red", fontSize: "14px" }}>
                                            {errors.category_id}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{ fontSize: "18px", fontWeight: "500" }}>
                                        Sub Category
                                    </Typography>
                                    <Select
                                        name="sub_category_id"
                                        value={badgeData.sub_category_id}
                                        onChange={handleSubCategoryChange}
                                        displayEmpty
                                        disabled={!badgeData.category_id}
                                    >
                                        <MenuItem value="" disabled>
                                            Select Sub Category
                                        </MenuItem>

                                        {subCategories.map((sub) => (
                                            <MenuItem key={sub.sub_cat_id} value={sub.sub_cat_id}>
                                                {sub.sub_cat_name}
                                            </MenuItem>
                                        ))}
                                    </Select>

                                    {errors.sub_category_id && (
                                        <Typography sx={{ color: "red", fontSize: "14px" }}>
                                            {errors.sub_category_id}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
                                    Badge Icon (optional)
                                </Typography>
                                <TextField
                                    type="file"
                                    inputProps={{ accept: "image/*" }}
                                    onChange={handleFileChange}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>XP reward</Typography>
                                <TextField
                                    name="xp_points"
                                    type="number"
                                    placeholder="Enter xp points"
                                    fullWidth
                                    value={badgeData.xp_points}
                                    onChange={handleChange}
                                />
                                {errors.xp_points && (
                                    <Typography sx={{ color: "red", fontSize: "14px" }}>
                                        {errors.xp_points}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Status</Typography>
                                    <Select
                                        name="is_active"
                                        value={badgeData.is_active}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select Status
                                        </MenuItem>
                                        <MenuItem value="1">Active</MenuItem>
                                        <MenuItem value="0">In-Active</MenuItem>
                                    </Select>

                                    {errors.is_active && (
                                        <Typography sx={{ color: "red", fontSize: "14px" }}>
                                            {errors.is_active}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Who can earn</Typography>
                                    <Select
                                        name="user_type"
                                        value={badgeData.user_type}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>
                                            Select User Type
                                        </MenuItem>
                                        {badgeData.criteria_type !== 7 && (
                                            <MenuItem value="user">User</MenuItem>
                                        )}
                                        <MenuItem value="trainer">Trainer</MenuItem>
                                    </Select>

                                    {errors.user_type && (
                                        <Typography sx={{ color: "red", fontSize: "14px" }}>
                                            {errors.user_type}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item md={12}>
                                <Typography sx={{mb: 1, fontSize: "18px", fontWeight: 500}}>
                                    Description
                                </Typography>
                                <textarea name="description" placeholder="Description" cols="5" rows="5"
                                          style={{
                                              width: "100%",
                                              minHeight: "150px",
                                              paddingTop: "15px",
                                              borderRadius: "10px",
                                              border: "1px solid #c4c4c4",
                                              paddingLeft: "15px",
                                              fontSize: "16px",
                                              outlineColor: "none"
                                          }}
                                          value={badgeData.description}
                                          onChange={handleChange}
                                ></textarea>

                                {errors.description && (
                                    <Typography sx={{ color: "red", fontSize: "14px" }}>
                                        {errors.description}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                                    <Button
                                        variant="contained"
                                        disabled={isSubmitting}
                                        sx={{
                                            background: "linear-gradient(181.45deg, #7AACFF -80.86%, rgba(60, 117, 213, 0.98) 98.77%)",
                                            color: "#fff",
                                            textTransform: "none",
                                            "&:hover": {
                                                background: "linear-gradient(181.45deg, #7AACFF -80.86%, rgba(60, 117, 213, 0.98) 98.77%)",
                                            },
                                        }}
                                        type="submit"
                                    >
                                        Save Badge
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
}

export default AddBadgeScreen;